import api from '@/libs/api'
import store from '@/store'

const projectId = () => store.state.app.projectId
const bucketName = () => store.state.uploading.bucketName
const currentUploadPath = () => store.state.uploading.currentUploadPath

export default {
  namespaced: true,
  state: {
    objects: [],
    object: null,
    objectsResultInfo: null,
    fetchingObjects: false,
    gettingObject: false,
    addingObject: false,
    deletingObject: false,
    updatingObject: false,
    refreshHelper: null,
    loadingMore: false,
    hasMore: false,
    generatingQuickShareUrl: false,
    eventTypes: null,
    jobStatusOptions: null,
    refreshingObjects: false,
    gettingDirectories: false,
    directories: [],
    gettingObjectMetadata: false,
    gettingObjectVersioning: false,
    deletingObjectVersion: false,
  },
  mutations: {
    // API
    SET_OBJECTS(state, data) {
      state.objects = data
    },
    SET_HAS_MORE(state, value) {
      state.hasMore = value
    },
    GET_OBJECT(state, object) {
      state.object = object
    },
    SET_FETCHING_OBJECTS(state, value) {
      state.fetchingObjects = value
    },
    SET_GETTING_OBJECT(state, value) {
      state.gettingObject = value
    },
    SET_ADDING_OBJECT(state, value) {
      state.addingObject = value
    },
    SET_DELETING_OBJECT(state, value) {
      state.deletingObject = value
    },
    SET_UPDATING_OBJECT(state, value) {
      state.updatingObject = value
    },
    SET_REFRESH_HELPER(state, value) {
      state.refreshHelper = value
    },
    SET_LOADING_MORE(state, value) {
      state.loadingMore = value
    },
    SET_GENERATING_QUICK_SHARE_URL(state, value) {
      state.generatingQuickShareUrl = value
    },
    REPLACE_OBJECT(state, object) {
      const index = state.objects.find(obj => obj.key === object.key)
      state.objects[index] = object
    },
    REMOVE_OBJECTS(state, objects) {
      const keys = [...objects.keys, ...objects.prefixes]
      state.objects = state.objects.filter(obj => !keys.includes(obj.key))
    },
    SET_EVENT_TYPES(state, value) {
      state.eventTypes = value
    },
    SET_JOB_STATUS_OPTIONS(state, value) {
      state.jobStatusOptions = value
    },
    REPLACE_NEW_KEYS(state, keys) {
      Object.keys(keys).forEach(key => {
        state.objects.find(obj => obj.key === key).key = keys[key]
      })
    },
    SET_REFRESHING_OBJECTS(state, value) {
      state.refreshingObjects = value
    },
    SET_DIRECTORIES(state, value) {
      state.directories = value
    },
    SET_GETTING_DIRECTORIES(state, value) {
      state.gettingDirectories = value
    },
    SET_GETTING_OBJECT_METADATA(state, value) {
      state.gettingObjectMetadata = value
    },
    SET_GETTING_OBJECT_VERSIONING(state, value) {
      state.gettingObjectVersioning = value
    },
    SET_DELETING_OBJECT_VERSION(state, value) {
      state.deletingObjectVersion = value
    },
  },
  actions: {
    getAllDirectories({ commit, state }) {
      commit('SET_GETTING_DIRECTORIES', true)
      if (state.refreshHelper) {
        const newQuery = {
          ...state.refreshHelper,
          maxKeys: 200,
        }
        return new Promise((resolve, reject) => {
          api('storage')
            .post(
              `/v1/projects/${projectId()}/buckets/${
                newQuery.bucketName
              }/objects/get`,
              newQuery,
            )
            .then(response => {
              commit('SET_DIRECTORIES', [
                ...response.data.result.objectListModel.filter(x => x.isFolder),
              ])
              resolve(response)
            })
            .catch(error => {
              reject(error)
            })
            .finally(() => {
              commit('SET_GETTING_DIRECTORIES', false)
            })
        })
      }
      return null
    },
    fetchObjects({ commit }, query) {
      commit('SET_FETCHING_OBJECTS', true)
      const { name } = query
      const { pageIndex } = query
      return new Promise((resolve, reject) => {
        api('storage')
          .get(
            `/v1/projects/${projectId()}/buckets/${name}/objects?PageIndex=${pageIndex}&PageSize=20`,
          )
          .then(response => {
            commit('SET_REFRESH_HELPER', query)
            commit('SET_OBJECTS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_OBJECTS', false)
          })
      })
    },
    addObject({ commit }, query) {
      commit('SET_ADDING_OBJECT', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .post(
            `/v1/projects/${query.projectId || projectId()}/buckets/${
              query.name
            }/objects`,
            query.object,
          )
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_ADDING_OBJECT', false)
          })
      })
    },
    updateObject({ commit }, query) {
      commit('SET_UPDATING_OBJECT', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .put(
            `/v1/projects/${projectId()}/buckets/${
              query.bucketName
            }/objects/${encodeURIComponent(query.key)}/properties`,
            query,
          )
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_UPDATING_OBJECT', false)
          })
      })
    },
    updateObjectKeys({ commit }, query) {
      commit('SET_UPDATING_OBJECT', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .put(
            `/v1/projects/${projectId()}/buckets/${query.bucketName}/objects/move`,
            query.updateObj,
          )
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_UPDATING_OBJECT', false)
          })
      })
    },
    getObject({ commit }, query) {
      commit('SET_GETTING_OBJECT', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .post(
            `/v1/projects/${projectId()}/buckets/${query.bucketName}/objects/get`,
            query,
          )
          .then(response => {
            commit(
              'SET_HAS_MORE',
              response.data.result.objectListModel.length >= 20,
            )
            commit('SET_OBJECTS', response.data.result.objectListModel)
            commit(
              'SET_EVENT_TYPES',
              response.data.result.storageEventTypeOptions,
            )
            commit(
              'SET_JOB_STATUS_OPTIONS',
              response.data.result.jobStatusOptions,
            )
            commit('SET_REFRESH_HELPER', query)
            commit('GET_OBJECT', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GETTING_OBJECT', false)
          })
      })
    },
    replaceObject({ commit }, object) {
      commit('REPLACE_OBJECT', object)
    },
    replaceNewKeys({ commit }, object) {
      commit('REPLACE_NEW_KEYS', {
        ...object.keys,
        ...object.prefixes,
      })
    },
    prependObjects({ commit, state }, arr) {
      const oldArr = [...state.objects]
      const newArr = []
      arr.forEach(el => {
        if (bucketName() === el.bucketName && currentUploadPath() === el.uploadPath && el.manuallyAdded) {
          const index = oldArr.findIndex(obj => obj.key === el.key)
          const secIndex = newArr.findIndex(obj => obj.key === el.key)
          if (index >= 0) {
            oldArr.splice(index, 1, el)
          } else if (secIndex < 0) {
            newArr.push(el)
          }
        }
      })
      commit('SET_OBJECTS', [...newArr, ...oldArr])
    },
    getMoreObjects({ commit, state }) {
      commit('SET_LOADING_MORE', true)

      if (state.refreshHelper) {
        const newQuery = {
          ...state.refreshHelper,
          continuationToken: state.objects[state.objects.length - 1].key,
        }
        return new Promise((resolve, reject) => {
          api('storage')
            .post(
              `/v1/projects/${projectId()}/buckets/${
                newQuery.bucketName
              }/objects/get`,
              newQuery,
            )
            .then(response => {
              commit(
                'SET_HAS_MORE',
                response.data.result.objectListModel.length >= 10,
              )
              commit('SET_OBJECTS', [
                ...state.objects,
                ...response.data.result.objectListModel.filter(
                  x => !state.objects.some(obj => obj.key === x.key),
                ),
              ])
              commit('SET_REFRESH_HELPER', newQuery)
              resolve(response)
            })
            .catch(error => {
              reject(error)
            })
            .finally(() => {
              commit('SET_LOADING_MORE', false)
            })
        })
      }
      return null
    },
    // eslint-disable-next-line no-empty-pattern
    fetchObjectInfo({}, objectInfoQuery) {
      const versionId = objectInfoQuery.versionId
        ? `?versionId=${objectInfoQuery.versionId}`
        : ''
      return api('storage').get(
        `/v1/projects/${projectId()}/buckets/${
          objectInfoQuery.bucketName
        }/objects/${encodeURIComponent(
          objectInfoQuery.key,
        )}/properties${versionId}`,
      )
    },
    deleteObjects({ commit }, query) {
      commit('SET_DELETING_OBJECT', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .delete(
            `/v1/projects/${projectId()}/buckets/${query.bucketName}/objects`,
            { data: { ...query.toDelete } },
          )
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_DELETING_OBJECT', false)
          })
      })
    },
    refreshList({ state, dispatch }) {
      if (state.refreshHelper) {
        dispatch('getObject', state.refreshHelper)
      }
    },
    generateQuickShareUrl({ commit }, query) {
      commit('SET_GENERATING_QUICK_SHARE_URL', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .post(
            `/v1/projects/${projectId()}/buckets/${query.bucketName}/objects/url`,
            query,
          )
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_GENERATING_QUICK_SHARE_URL', false)
          })
      })
    },
    refreshObjects({ commit, state }, extraKeys = 0) {
      commit('SET_REFRESHING_OBJECTS', true)
      const max = state.objects.length + extraKeys
      if (state.refreshHelper) {
        const newQuery = {
          ...state.refreshHelper,
          continuationToken: null,
          maxKeys: max < 20 ? 20 : max,
        }
        return new Promise((resolve, reject) => {
          api('storage')
            .post(
              `/v1/projects/${projectId()}/buckets/${
                newQuery.bucketName
              }/objects/get`,
              newQuery,
            )
            .then(response => {
              commit('SET_OBJECTS', [...response.data.result.objectListModel])
              resolve(response)
            })
            .catch(error => {
              reject(error)
            })
            .finally(() => {
              commit('SET_REFRESHING_OBJECTS', false)
            })
        })
      }
      return null
    },
    allObjectsAvailable({ state }) {
      return !state.objects.some(
        item => !(
          item.eventType === state.eventTypes.Default
            && item.status === state.jobStatusOptions.Available
        ),
      )
    },
    refreshUntilStatusCleared({ commit, state, dispatch }, extraKeys = 0) {
      commit('SET_REFRESHING_OBJECTS', true)
      const max = state.objects.length + extraKeys
      if (state.refreshHelper) {
        const newQuery = {
          ...state.refreshHelper,
          continuationToken: null,
          maxKeys: max < 10 ? 10 : max,
        }
        return new Promise((resolve, reject) => {
          api('storage')
            .post(
              `/v1/projects/${projectId()}/buckets/${
                newQuery.bucketName
              }/objects/get`,
              newQuery,
            )
            .then(response => {
              commit('SET_OBJECTS', [...response.data.result.objectListModel])
              dispatch('allObjectsAvailable').then(ok => {
                if (!ok) {
                  setTimeout(() => {
                    dispatch('refreshUntilStatusCleared')
                  }, 3000)
                }
              })
              resolve(response)
            })
            .catch(error => {
              reject(error)
            })
            .finally(() => {
              commit('SET_REFRESHING_OBJECTS', false)
            })
        })
      }
      return null
    },
    getObjectVersioning({ commit }, objectInfoQuery) {
      commit('SET_GETTING_OBJECT_VERSIONING', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .get(
            `/v1/projects/${projectId()}/buckets/${
              objectInfoQuery.bucketName
            }/objects/${encodeURIComponent(objectInfoQuery.key)}`,
          )
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_GETTING_OBJECT_VERSIONING', false)
          })
      })
    },
    deleteObjectVersion({ commit }, objectInfoQuery) {
      commit('SET_DELETING_OBJECT_VERSION', true)
      return new Promise((resolve, reject) => {
        api('storage')
          .delete(
            `/v1/projects/${projectId()}/buckets/${
              objectInfoQuery.bucketName
            }/objects/${encodeURIComponent(objectInfoQuery.key)}?versionId=${
              objectInfoQuery.versionId
            }`,
          )
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_DELETING_OBJECT_VERSION', false)
          })
      })
    },
  },
}
